/**
 * features/attendance/GridAttendance.tsx
 * ------------------------------------------------------------------
 * Weekly / monthly attendance BACKFILL grid.
 *
 * A fast way for a class teacher to fill in registers they didn't take
 * day by day: pick the class, then a week or a month.
 *
 * A day that has NOT been recorded shows BLANK (no register is created
 * for it). To record a day, tick its column checkbox (or "All present"
 * for the whole period) — everyone starts PRESENT; the teacher then
 * unchecks the pupils who were absent. Only ticked days are saved.
 *
 * Present/absent only. Richer statuses (late / excused / sick) and per-
 * pupil notes stay on the Daily tab; any such status already recorded for
 * a day is PRESERVED here unless the teacher toggles that exact cell.
 * ------------------------------------------------------------------ */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';
import { Save as SaveIcon, ChevronLeft, ChevronRight, CheckCircle2, AlertCircle, CalendarRange } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { auditFromContext } from '@/lib/audit';
import { canEnterClassCommentFor } from '@/lib/permissions';
import { usePublicHolidays } from '@/features/hr/usePublicHolidays';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { GradientHero, SoftCard, glassFieldClass } from '@/components/ui/glass';
import type { AttendanceStatus, DailyAttendanceDoc } from '@/types';

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function todayISO(): string {
  return ymd(new Date());
}

/** Weekday (Mon–Fri) dates in the selected week or month, capped at today. */
function buildDays(anchorISO: string, period: 'week' | 'month'): string[] {
  const a = new Date(anchorISO + 'T12:00:00');
  const today = todayISO();
  const out: string[] = [];
  if (period === 'week') {
    const wd = a.getDay();
    const monday = new Date(a);
    monday.setDate(a.getDate() + (wd === 0 ? -6 : 1 - wd));
    for (let i = 0; i < 5; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const s = ymd(d);
      if (s <= today) out.push(s);
    }
  } else {
    const y = a.getFullYear();
    const m = a.getMonth();
    const last = new Date(y, m + 1, 0).getDate();
    for (let day = 1; day <= last; day++) {
      const d = new Date(y, m, day, 12);
      const w = d.getDay();
      if (w !== 0 && w !== 6) {
        const s = ymd(d);
        if (s <= today) out.push(s);
      }
    }
  }
  return out;
}

function periodLabel(anchorISO: string, period: 'week' | 'month'): string {
  const a = new Date(anchorISO + 'T12:00:00');
  if (period === 'month') return a.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const days = buildDays(anchorISO, 'week');
  const first = new Date((days[0] ?? anchorISO) + 'T12:00:00');
  return `Week of ${first.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

const colHead = (iso: string) => {
  const d = new Date(iso + 'T12:00:00');
  return { dow: d.toLocaleDateString('en-GB', { weekday: 'short' }), dom: d.getDate() };
};

export default function GridAttendance({ period }: { period: 'week' | 'month' }) {
  const { activeStudents: students, classes, teachers } = useData();
  const { userData } = useAuth();

  const [classId, setClassId] = useState('');
  const [anchor, setAnchor] = useState(todayISO());
  // Status for a cell, only meaningful for ACTIVE (recorded) days.
  const [grid, setGrid] = useState<Record<string, Record<string, AttendanceStatus>>>({});
  // Days being recorded this session (existing registers start active; blank
  // days are inactive until the teacher ticks them).
  const [active, setActive] = useState<Set<string>>(new Set());
  // What Firestore held for each visible day (to restore on re-activate).
  const [loaded, setLoaded] = useState<Record<string, Record<string, AttendanceStatus>>>({});
  const [createdAt, setCreatedAt] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });

  const myClasses = useMemo(
    () => classes.filter((c) => canEnterClassCommentFor(userData, c.id, classes, teachers)),
    [classes, userData, teachers],
  );
  useEffect(() => {
    if (!classId && myClasses.length === 1) setClassId(myClasses[0].id);
  }, [classId, myClasses]);

  const classObj = classes.find((c) => c.id === classId);
  const roster = useMemo(
    () => students.filter((s) => s.currentClass === classId).sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [students, classId],
  );
  // buildDays already drops weekends; also drop public holidays (live,
  // admin-managed list) so no holiday column can be recorded by mistake.
  const holidays = usePublicHolidays();
  const days = useMemo(
    () => buildDays(anchor, period).filter((d) => !holidays.isHoliday(d)),
    [anchor, period, holidays],
  );

  const cellStatus = (date: string, sid: string): AttendanceStatus => grid[date]?.[sid] ?? 'present';
  const isPresent = (date: string, sid: string) => cellStatus(date, sid) === 'present';

  /* ---- load existing registers for the visible range ---- */
  const load = useCallback(async () => {
    if (!classId || days.length === 0) {
      setGrid({}); setActive(new Set()); setLoaded({});
      return;
    }
    setLoading(true);
    try {
      const wanted = new Set(days);
      const snap = await getDocs(
        query(collection(db, COLLECTIONS.dailyAttendance), where('classId', '==', classId)),
      );
      const byDate: Record<string, Record<string, AttendanceStatus>> = {};
      const created: Record<string, string> = {};
      snap.docs.forEach((d) => {
        const data = d.data() as DailyAttendanceDoc;
        if (data.date && wanted.has(data.date)) {
          byDate[data.date] = { ...(data.entries ?? {}) };
          if (data.createdAt) created[data.date] = data.createdAt;
        }
      });
      // Existing registers start ACTIVE with their recorded statuses; every
      // other day stays BLANK (inactive) until the teacher ticks it.
      const activeDates = new Set(Object.keys(byDate));
      const g: Record<string, Record<string, AttendanceStatus>> = {};
      for (const date of activeDates) {
        g[date] = {};
        for (const s of roster) g[date][s.id] = byDate[date]?.[s.id] ?? 'present';
      }
      setLoaded(byDate);
      setGrid(g);
      setActive(activeDates);
      setCreatedAt(created);
      setDirty(false);
    } finally {
      setLoading(false);
    }
  }, [classId, days, roster]);

  useEffect(() => { load(); }, [load]);

  /* ---- activate / clear days ---- */
  function activateDay(date: string) {
    setActive((prev) => new Set(prev).add(date));
    setGrid((prev) => {
      const col: Record<string, AttendanceStatus> = {};
      roster.forEach((s) => (col[s.id] = loaded[date]?.[s.id] ?? 'present'));
      return { ...prev, [date]: col };
    });
    setDirty(true);
  }
  function clearDay(date: string) {
    setActive((prev) => { const n = new Set(prev); n.delete(date); return n; });
    setGrid((prev) => { const n = { ...prev }; delete n[date]; return n; });
    setDirty(true);
  }
  function toggleDay(date: string) {
    if (active.has(date)) clearDay(date); else activateDay(date);
  }
  function setAllDaysPresent() {
    const g: Record<string, Record<string, AttendanceStatus>> = {};
    days.forEach((date) => {
      g[date] = {};
      roster.forEach((s) => (g[date][s.id] = loaded[date]?.[s.id] ?? 'present'));
    });
    setGrid(g);
    setActive(new Set(days));
    setDirty(true);
  }
  function clearAllDays() {
    setGrid({});
    setActive(new Set());
    setDirty(true);
  }

  /* ---- edits (only on active days) ---- */
  function toggleCell(date: string, sid: string) {
    if (!active.has(date)) return;
    setGrid((prev) => {
      const cur = prev[date]?.[sid] ?? 'present';
      const next: AttendanceStatus = cur === 'present' ? 'absent' : 'present';
      return { ...prev, [date]: { ...prev[date], [sid]: next } };
    });
    setDirty(true);
  }
  function flipColumn(date: string) {
    if (!active.has(date)) return;
    const allPresent = roster.every((s) => isPresent(date, s.id));
    setGrid((prev) => {
      const col: Record<string, AttendanceStatus> = {};
      roster.forEach((s) => (col[s.id] = allPresent ? 'absent' : 'present'));
      return { ...prev, [date]: col };
    });
    setDirty(true);
  }

  /* ---- save: one doc per ACTIVE day ---- */
  async function handleSave() {
    const saveDays = days.filter((d) => active.has(d));
    if (!classId || saveDays.length === 0) return;
    setSaving(true);
    setSaveModal({ show: true, status: 'saving', message: `Saving ${saveDays.length} day(s)…` });
    try {
      const now = new Date().toISOString();
      const batch = writeBatch(db);
      let absentTotal = 0;
      for (const date of saveDays) {
        const entries: Record<string, AttendanceStatus> = {};
        roster.forEach((s) => {
          const st = grid[date]?.[s.id] ?? 'present';
          entries[s.id] = st;
          if (st !== 'present') absentTotal += 1;
        });
        const payload: DailyAttendanceDoc = {
          classId,
          className: classObj?.name ?? classId,
          date,
          entries,
          recordedBy: userData?.uid ?? 'system',
          recordedByName: userData?.displayName ?? 'System',
          createdAt: createdAt[date] ?? now,
          updatedAt: now,
        };
        batch.set(doc(db, COLLECTIONS.dailyAttendance, `${classId}_${date}`), payload);
      }
      await batch.commit();
      setDirty(false);
      setSaveModal({ show: true, status: 'success', message: `Saved ${saveDays.length} day(s) for ${classObj?.name ?? classId}.` });
      auditFromContext(userData, 'attendance_backfill', 'attendance', {
        targetId: classId,
        targetType: 'attendance',
        targetName: classObj?.name ?? classId,
        details: { period, from: saveDays[0], to: saveDays[saveDays.length - 1], days: saveDays.length, absentMarks: absentTotal },
      });
      setLoaded((prev) => {
        const n = { ...prev };
        saveDays.forEach((d) => { n[d] = { ...grid[d] }; });
        return n;
      });
      setCreatedAt((prev) => {
        const n = { ...prev };
        saveDays.forEach((d) => { if (!n[d]) n[d] = now; });
        return n;
      });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  const shift = (dir: number) => {
    const a = new Date(anchor + 'T12:00:00');
    if (period === 'week') a.setDate(a.getDate() + dir * 7);
    else a.setMonth(a.getMonth() + dir);
    setAnchor(ymd(a));
  };
  const mondayOf = (d: Date) => {
    const m = new Date(d);
    m.setDate(d.getDate() + (d.getDay() === 0 ? -6 : 1 - d.getDay()));
    return m;
  };
  const atFuture = () => {
    const a = new Date(anchor + 'T12:00:00');
    const now = new Date();
    if (period === 'month') return a.getFullYear() > now.getFullYear() || (a.getFullYear() === now.getFullYear() && a.getMonth() >= now.getMonth());
    return ymd(mondayOf(a)) >= ymd(mondayOf(now));
  };

  const activeCount = days.filter((d) => active.has(d)).length;
  const allActive = days.length > 0 && activeCount === days.length;

  return (
    <div className="space-y-4">
      <GradientHero
        icon={CalendarRange}
        title={period === 'week' ? 'Weekly attendance' : 'Monthly attendance'}
        subtitle={classId ? `${classObj?.name ?? classId} · ${periodLabel(anchor, period)}` : 'Select a class to begin'}
      />

      <SoftCard pad="p-5">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Class</label>
            {myClasses.length > 1 ? (
              <select value={classId} onChange={(e) => setClassId(e.target.value)} className={glassFieldClass()}>
                <option value="">— select class —</option>
                {myClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            ) : (
              <div className={`${glassFieldClass()} flex items-center`}>{myClasses[0]?.name ?? 'No class assigned'}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => shift(-1)} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"><ChevronLeft size={16} /></button>
            <div className="min-w-[190px] text-center text-sm font-semibold text-gray-700 dark:text-gray-200">{periodLabel(anchor, period)}</div>
            <button onClick={() => shift(1)} disabled={atFuture()} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700 disabled:opacity-30"><ChevronRight size={16} /></button>
            <button onClick={() => setAnchor(todayISO())} className="text-xs text-[#2d5295] hover:underline ml-1">This {period === 'week' ? 'week' : 'month'}</button>
          </div>
        </div>
      </SoftCard>

      <div className="text-sm rounded-xl px-4 py-3 border bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200 flex items-start gap-2">
        <AlertCircle size={15} className="shrink-0 mt-0.5" />
        <span>
          A day that hasn't been recorded is <b>blank</b> — nothing is saved for it. <b>Tick a day's box</b> (in its
          column header) to record it, or <b>All present</b> to record the whole {period}: everyone starts <b>present</b>,
          then uncheck the pupils who were absent. Only ticked days are saved. Use the <b>Daily</b> tab for late / excused / sick.
          {' '}<b>Weekends and public holidays are left out</b> — they can't be recorded.
        </span>
      </div>

      {!classId && <SoftCard><p className="text-center text-gray-400 py-8">Select a class to begin.</p></SoftCard>}
      {classId && loading && <Spinner label="Loading registers…" />}
      {classId && !loading && roster.length === 0 && <SoftCard><p className="text-center text-gray-400 py-8">No students in this class.</p></SoftCard>}
      {classId && !loading && days.length === 0 && <SoftCard><p className="text-center text-gray-400 py-8">No school days to show in this {period}.</p></SoftCard>}

      {classId && !loading && roster.length > 0 && days.length > 0 && (
        <SoftCard pad="p-0">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
            <span className="text-xs text-gray-500">{roster.length} pupils · {activeCount} of {days.length} day(s) recorded</span>
            <div className="flex items-center gap-2">
              <button onClick={setAllDaysPresent} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 font-medium">All present</button>
              <button onClick={clearAllDays} className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 font-medium">Clear all</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="text-sm border-collapse">
              <thead>
                <tr className="text-gray-500 dark:text-gray-400">
                  <th className="sticky left-0 z-10 bg-white dark:bg-gray-900 text-left px-3 py-2 font-medium min-w-[160px] border-b border-gray-100 dark:border-gray-700">
                    <label className="flex items-center gap-1.5 cursor-pointer" title="Record every day this period (all present)">
                      <input type="checkbox" checked={allActive} onChange={() => (allActive ? clearAllDays() : setAllDaysPresent())} className="w-3.5 h-3.5" />
                      <span className="text-xs">Pupil</span>
                    </label>
                  </th>
                  {days.map((d) => {
                    const h = colHead(d);
                    const on = active.has(d);
                    return (
                      <th key={d} className={`px-1.5 py-1.5 text-center border-b border-gray-100 dark:border-gray-700 ${on ? '' : 'opacity-50'}`}>
                        <label className="flex items-center justify-center mb-0.5 cursor-pointer" title={on ? 'Recorded — untick to leave blank' : 'Tick to record this day (all present)'}>
                          <input type="checkbox" checked={on} onChange={() => toggleDay(d)} className="w-3.5 h-3.5" />
                        </label>
                        <button onClick={() => flipColumn(d)} disabled={!on} className="block w-full leading-tight hover:text-[#2d5295] disabled:cursor-default" title={on ? 'Flip whole day' : ''}>
                          <span className="text-[10px] uppercase">{h.dow}</span><br />
                          <span className="text-sm font-semibold">{h.dom}</span>
                        </button>
                      </th>
                    );
                  })}
                  <th className="px-2 py-2 text-center text-[10px] font-medium border-b border-gray-100 dark:border-gray-700">P/T</th>
                </tr>
              </thead>
              <tbody>
                {roster.map((s, i) => {
                  const presentCount = days.filter((d) => active.has(d) && isPresent(d, s.id)).length;
                  return (
                    <tr key={s.id} className={i % 2 ? 'bg-gray-50/50 dark:bg-gray-800/20' : ''}>
                      <td className="sticky left-0 z-10 bg-inherit px-3 py-1.5 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">
                        <span className="text-xs text-gray-400 mr-1.5">{i + 1}</span>{s.fullName}
                      </td>
                      {days.map((d) => {
                        const on = active.has(d);
                        if (!on) {
                          return (
                            <td key={d} className="px-1 py-1 text-center">
                              <button onClick={() => activateDay(d)} title="Blank — click to start recording this day" className="w-6 h-6 rounded-md border border-dashed border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600">·</button>
                            </td>
                          );
                        }
                        const st = cellStatus(d, s.id);
                        const present = st === 'present';
                        return (
                          <td key={d} className="px-1 py-1 text-center">
                            <button
                              onClick={() => toggleCell(d, s.id)}
                              title={`${s.fullName} · ${d} · ${st}`}
                              className={`w-6 h-6 rounded-md border flex items-center justify-center text-xs font-bold transition-colors ${
                                present
                                  ? 'bg-emerald-500 border-emerald-500 text-white'
                                  : st === 'absent'
                                    ? 'bg-red-50 border-red-300 text-red-500 dark:bg-red-900/30'
                                    : 'bg-amber-50 border-amber-300 text-amber-600 dark:bg-amber-900/30'
                              }`}
                            >
                              {present ? '✓' : st === 'absent' ? '' : st.charAt(0).toUpperCase()}
                            </button>
                          </td>
                        );
                      })}
                      <td className="px-2 py-1 text-center text-xs text-gray-500 whitespace-nowrap">{presentCount}/{activeCount}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SoftCard>
      )}

      {dirty && classId && (
        <div className="sticky bottom-4 z-30">
          <button onClick={handleSave} disabled={saving || activeCount === 0} className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#1b3a6b] text-white font-semibold rounded-xl shadow-2xl hover:bg-[#2d5295] active:scale-[0.98] transition-all disabled:opacity-50">
            <SaveIcon size={20} />
            {saving ? 'Saving…' : activeCount === 0 ? 'Tick a day to record' : `Save ${activeCount} day(s)`}
          </button>
        </div>
      )}

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />

      {classId && !loading && days.length > 0 && (
        <p className="text-xs text-gray-400 flex items-center gap-1"><CheckCircle2 size={12} /> Dashed · = blank (not recorded) · green ✓ = present · empty red = absent · amber = late/excused/sick from the Daily tab (kept unless changed).</p>
      )}
    </div>
  );
}
